import * as React from "react";
import { useState } from "react";
import { KeysMatching, useDebugSettings } from "conference/tools";
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    Divider,
    FormControl,
    FormControlLabel,
    TextField,
} from "@mui/material";
import { DialogTitle } from "conference/components/dialogs";
import { DebugSettings, defaultDebugSettings } from "./types";
import { defineMessages, FormattedMessage } from "react-intl";

const debugOptionMessages = defineMessages({
    disableRoomLeaveBlocker: {
        id: "debug.controlPanel.disableRoomLeaveBlocker",
        defaultMessage: "Disable warning if you want to close the meeting window",
    },
    enableAllBrowsers: { id: "debug.controlPanel.enableAllBrowsers", defaultMessage: "Enable all browsers" },
    showRestartActiveMeetingPageButton: {
        id: "debug.controlPanel.showRestartActiveMeetingPageButton",
        defaultMessage: "Show restart button in goodbye page",
    },
    showVideoInfo: { id: "debug.controlPanel.showVideoInfo", defaultMessage: "Show video debug info panel" },
    enabled: { id: "debug.enabled", defaultMessage: "Debug mode is enabled!" },
    showSpeakerListInfoDialog: {
        id: "debug.controlPanel.showSpeakerListInfoDialog",
        defaultMessage: "Show speaker list info dialog",
    },
});

const DebugSwitch = ({ option }: { option: keyof DebugSettings & KeysMatching<DebugSettings, boolean> }) => {
    const debugSettings = useDebugSettings();

    return (
        <FormControlLabel
            control={
                <Checkbox
                    color="primary"
                    checked={debugSettings[option]}
                    onChange={() => (debugSettings[option] = !debugSettings[option])}
                />
            }
            label={<FormattedMessage {...debugOptionMessages[option]} />}
            disabled={!debugSettings.enabled}
        />
    );
};

function debugSettingsEntries(settings: DebugSettings) {
    return Object.entries(settings) as [keyof DebugSettings, DebugSettings[keyof DebugSettings]][];
}

export default ({ show, hide }: { show: boolean; hide: () => void }) => {
    const debugSettings = useDebugSettings();
    const [loggerNamespaces, setLoggerNamespaces] = useState(debugSettings.loggerNamespaces);

    const isResetEnabled =
        Math.max(
            ...debugSettingsEntries(defaultDebugSettings).map(([key, value]) => (debugSettings[key] === value ? 0 : 1)),
        ) > 0;

    const onReset = () => {
        for (const [key, value] of debugSettingsEntries(defaultDebugSettings)) {
            if (debugSettings[key] !== value) (debugSettings[key] as any) = value;
        }
        if (loggerNamespaces !== defaultDebugSettings.loggerNamespaces)
            setLoggerNamespaces(defaultDebugSettings.loggerNamespaces);
    };

    return (
        <>
            <Dialog open={show} onClose={hide}>
                <DialogTitle>
                    <FormattedMessage id="debug.controlPanel.title" defaultMessage="Debug control panel" />
                </DialogTitle>
                <DialogContent style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ flexGrow: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={debugSettings.enabled}
                                    onChange={() => (debugSettings.enabled = !debugSettings.enabled)}
                                />
                            }
                            label={<FormattedMessage id="debug.controlPanel.modeSwitch" defaultMessage="Enabled" />}
                        />
                    </div>
                    <div>
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={onReset}
                            disabled={!isResetEnabled}
                        >
                            <FormattedMessage id="debug.controlPanel.resetButton" defaultMessage="Reset" />
                        </Button>
                    </div>
                </DialogContent>
                <Divider />
                <DialogContent style={{ marginBottom: 10, paddingTop: 20 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={
                            <FormattedMessage
                                id="debug.controlPanel.loggerNamespaces"
                                defaultMessage="Logger namespaces"
                            />
                        }
                        value={loggerNamespaces}
                        onChange={(ev) => setLoggerNamespaces(ev.target.value)}
                        onBlur={() => (debugSettings.loggerNamespaces = loggerNamespaces)}
                        disabled={!debugSettings.enabled}
                    />
                    <FormControl style={{ marginTop: 10 }}>
                        <DebugSwitch option="disableRoomLeaveBlocker" />
                        <DebugSwitch option="showRestartActiveMeetingPageButton" />
                        <DebugSwitch option="enableAllBrowsers" />
                        <DebugSwitch option="showVideoInfo" />
                    </FormControl>
                </DialogContent>
            </Dialog>
        </>
    );
};
